import React, { memo } from "react";

import { ImageElement } from "@kontent-ai/gatsby-components";
import classNames from "classnames";
import { graphql } from "gatsby";
import PropTypes from "prop-types";

import ImagePlaceholder from "../components/imagePlaceholder/imagePlaceholder";
import MediaLightboxImage from "../components/lightbox/mediaLightboxImage";

const KontentItemImage = ({
  data = undefined,
  fixed = false,
  height = null,
  width = null,
  style = null,
  className = "",
  enableComponentStyle = false,
  directDescription = "",
  defaultImage = false,
  aspectRatio = null,
  imageOnlyWidth = false,
  fit = null,
  placeholderTyp = null,
}) => {
  let image = null;
  let imageWidth = width;
  let imageHeight = height;
  let description = directDescription;
  let defaultAspectRatio = null;
  let extraClasses = "";
  let imgStyle = null;
  let isModalEnabled = false;
  let originalHeight = null;
  let originalWidth = null;
  let isGraphic = false;

  let imageValue = null;

  image = data?.value ? data.value[0] : data;

  if (!image) {
    if (height || width) {
      return (
        <ImagePlaceholder
          height={height}
          width={width}
          aspectRatio={aspectRatio}
          className={className}
          typ={placeholderTyp}
        />
      );
    }

    return null;
  }

  if (image && image.elements) {
    if (image.elements.image.value.length === 0) {
      return null;
    }

    [imageValue] = image.elements.image.value;

    let tmpRatio = 1;

    if (imageValue.width > 1920) {
      tmpRatio = imageValue.height / imageValue.width;

      imageValue.width = 1920;
      imageValue.height = tmpRatio * 1920;
    }

    if (imageValue.height > 2880) {
      tmpRatio = imageValue.width / imageValue.height;

      imageValue.height = 2880;
      imageValue.width = tmpRatio * 2880;
    }

    defaultAspectRatio = imageValue.width / imageValue.height;
    originalWidth = imageValue.width;
    originalHeight = imageValue.height;
    description = imageValue.description;

    if (enableComponentStyle && image.elements.image_component_style.value.length > 0) {
      switch (image.elements.image_component_style.value[0].codename) {
        case "panorama_21_9":
          imageWidth = 1920;
          defaultAspectRatio = 21 / 9;
          extraClasses = "text-section__fullwidth";
          break;
        case "flexible_height":
          imageWidth = 1000;
          break;
        case "block_image_right":
          extraClasses = "float-right mt-0 p-0 pl-30 col-12 col-md-5";
          imageWidth = 400;
          isModalEnabled = true;
          break;
        case "block_image_left":
          extraClasses = "float-left mt-0 p-0 pr-30 col-12 col-md-5";
          imageWidth = 400;
          isModalEnabled = true;
          break;
        case "upright_3_4":
          extraClasses = "col-md-6 mx-auto";
          imageWidth = 400;
          imageHeight = (imageWidth / 3) * 4;
          isModalEnabled = true;
          break;
        default:
      }
    }

    const mediaType = image.elements.media_type.value;
    isGraphic = mediaType.length > 0 && mediaType[0].codename === "graphic";

    if (isGraphic) {
      imgStyle = {
        objectFit: "contain",
      };
    }

    if (image.elements.set_focal_point__image_transformer.value) {
      const focalPoint = JSON.parse(image.elements.set_focal_point__image_transformer.value);

      if (focalPoint.length > 0) {
        const kontentFocalPoint = focalPoint[0].transforms.crop.zoom;

        if (kontentFocalPoint) {
          imgStyle = {
            objectPosition: `${Math.abs(kontentFocalPoint.xFloat * 100)}% ${Math.abs(kontentFocalPoint.yFloat * 100)}%`,
            ...imgStyle,
          };
        }
      }
    }
  }

  if (imageWidth && imageHeight) {
    defaultAspectRatio = imageWidth / imageHeight;
  }

  let defaultStyle = null;

  if (
    imageHeight
    && !fixed
    && (!imageOnlyWidth || ((originalHeight * imageWidth) / originalWidth) < imageHeight)
  ) {
    imageWidth = width * 3;
    defaultStyle = {
      height,
      width,
    };
  }

  if (
    imageWidth
    && (!imageOnlyWidth || ((originalHeight * imageWidth) / originalWidth) >= imageHeight)
  ) {
    imageHeight = height * 3;
    defaultStyle = {
      height,
      width,
    };
  }

  const imageProps = {
    alt: description,
    aspectRatio: aspectRatio || defaultAspectRatio,
    className,
    height: imageHeight,
    image: imageValue,
    layout: "constrained",
    style: style || defaultStyle,
    title: description,
    width: imageWidth,
  };

  if (fixed) {
    return (
      <ImageElement
        {...imageProps}
        imgStyle={imgStyle}
        objectFit="cover"
        options={{
          fit: fit || "clip",
          lossless: true,
        }}
      />
    );
  }

  if (defaultImage || (!imageHeight && !imageWidth)) {
    if (typeof imageValue !== "object" || !imageValue) {
      return (<>MISSING IMAGE</>);
    }

    return (
      <ImageElement
        {...imageProps}
        // imgStyle={imgStyle}
        options={{
          fit: fit || "crop",
          lossless: true,
        }}
      />
    );
  }

  const img = (
    <ImageElement
      {...imageProps}
      style={imageOnlyWidth ? style : null}
      imgStyle={imgStyle}
      options={{
        fit: fit || "cover",
        lossless: true,
      }}
    />
  );

  if (isModalEnabled) {
    let mediaModal = null;

    const showModal = () => {
      if (isModalEnabled && mediaModal) {
        mediaModal.show();
      }
    };

    return (
      <div
        tabIndex={0}
        role="button"
        className={classNames(extraClasses, "img-container")}
        onClick={() => showModal()}
        onKeyPress={
          (e) => {
            const char = e.charCode;

            if (char === 32 || char === 13) {
              showModal();
            } else {
              e.stopPropagation();
            }
          }
        }
      >
        {img}
        <MediaLightboxImage
          media={(
            <ImageElement
              {...imageProps}
              style={imageOnlyWidth ? style : null}
              layout="fullWidth"
            />
          )}
          ref={(modal) => {
            mediaModal = modal;
          }}
        />
      </div>
    );
  }

  return (
    <div className={classNames(extraClasses, "img-container")}>
      {img}
    </div>
  );
};

KontentItemImage.propTypes = {
  aspectRatio: PropTypes.number,
  className: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.object]),
  defaultImage: PropTypes.bool,
  directDescription: PropTypes.string,
  enableComponentStyle: PropTypes.bool,
  fit: PropTypes.string,
  fixed: PropTypes.bool,
  height: PropTypes.number,
  imageOnlyWidth: PropTypes.bool,
  placeholderTyp: PropTypes.oneOf(["events", "news"]),
  style: PropTypes.oneOfType([PropTypes.object]),
  width: PropTypes.number,
};

export const query = graphql`
  fragment KontentItemImage on kontent_item_image {
    id
    system {
      codename
    }
    elements {
      image {
        value {
          description
          size
          name
          type
          url
          width
          height
        }
      }
      set_focal_point__image_transformer {
        value
      }
      image_component_style {
        value {
          codename
        }
      }
      media_type {
        value {
          codename
        }
      }
    }
  }
`;

export default memo(KontentItemImage);
